import type { State } from '../StateTypes';
import type {
  IDexfSettingsKompis,
  IDexfSettingsLocalisation,
} from '@inter-ikea-kompis/types';
import type {
  IExtendedDexfSettings,
  IDexfSettingsAppsettings,
  IDexfSettingsVpc,
  IDexfSettingsGallery,
} from './dexfSettingsTypes';

/**
 * Select Dexf settings
 *
 * @param state
 * @returns {IExtendedDexfSettings} The Dexf settings
 */
export const selectDexfSettings = (state: State): IExtendedDexfSettings =>
  state.dexfSettings;

/**
 * Select Dexf settings have been loaded
 *
 * @param state
 * @returns {boolean} Whether the Dexf settings have been loaded
 */
export const selectDexfSettingsHaveBeenLoaded = (state: State): boolean =>
  Object.keys(state.dexfSettings).length > 0;

/**
 * Select localisation Dexf settings
 *
 * @param state
 * @returns {IDexfSettingsLocalisation} The localisation Dexf settings
 */
export const selectLocalisationDexfSettings = (
  state: State
): IDexfSettingsLocalisation => selectDexfSettings(state).localisation;

/**
 * Select Kompis Dexf settings
 *
 * @param state
 * @returns {IDexfSettingsKompis} The Kompis Dexf settings
 */
export const selectKompisDexfSettings = (state: State): IDexfSettingsKompis =>
  selectDexfSettings(state).kompis;

/**
 * Select appsettings Dexf settings
 *
 * @param state
 * @returns {IDexfSettingsAppsettings} The appsettings Dexf settings
 */
export const selectAppsettingsDexfSettings = (
  state: State
): IDexfSettingsAppsettings => selectDexfSettings(state).appsettings;

/**
 * Select VPC Dexf settings
 *
 * @param state
 * @returns {IDexfSettingsVpc} The VPC Dexf settings
 */
export const selectVpcDexfSettings = (state: State): IDexfSettingsVpc =>
  selectDexfSettings(state).vpc;

/**
 * Select Gallery Dexf settings
 *
 * @param state
 * @returns {IDexfSettingsGallery} The Gallery Dexf settings
 */
export const selectGalleryDexfSettings = (state: State): IDexfSettingsGallery =>
  selectDexfSettings(state).gallery;

/**
 * Select has tip over warning
 *
 * @param state
 * @returns {boolean}
 */
export const selectHasTipOverWarning = (state: State): boolean =>
  selectKompisDexfSettings(state).localization.hasTipOverWarning;

/**
 * Select tip over warning URL
 *
 * @param state
 * @returns {string | undefined}
 */
export const selectTipOverWarningUrl = (state: State): string | undefined =>
  selectKompisDexfSettings(state).urls.tipOverWarningUrl;

/**
 * Select use metric
 *
 * @param state
 * @returns {boolean}
 */
export const selectUseMetric = (state: State): boolean =>
  selectLocalisationDexfSettings(state).useMetricMeasures;

/**
 * Select write direction
 *
 * @param state
 * @returns {string}
 */
export const selectWriteDirection = (state: State): string =>
  selectLocalisationDexfSettings(state).writeDirection;

/**
 * Checks whether market uses right to left text
 *
 * @param state
 * @returns {boolean}
 */
export const selectIsRtl = (state: State): boolean =>
  selectWriteDirection(state) === 'rtl';

/**
 * Checks whether the supply banner is enabled for the market
 *
 * @param state
 * @returns {boolean}
 */
export const selectIsSupplyBannedEnabled = (state: State): boolean =>
  selectAppsettingsDexfSettings(state).plannerBannerMessageEnabled;

/**
 * Select message type for the supply banner
 *
 * @param state
 * @returns {string}
 */
export const selectSupplyBannerMessageType = (state: State): string =>
  selectAppsettingsDexfSettings(state).plannerBannerMessageType;

/**
 * Select link URL for the supply banner
 *
 * @param state
 * @returns {string}
 */
export const selectSupplyBannerLinkURL = (state: State): string =>
  selectAppsettingsDexfSettings(state).plannerBannerMessageLinkUrl;

/**
 * Select currency code of the market
 *
 * @param state
 * @returns {string}
 */
export const selectCurrencyCode = (state: State): string =>
  selectLocalisationDexfSettings(state).currencyCode;

/**
 * Select experience gallery back URL
 *
 * @param state
 * @returns {string}
 */
export const selectExperienceGalleryBackUrl = (state: State): string =>
  selectGalleryDexfSettings(state).experienceGalleryBackURL;

/**
 * Select CTE gallery back URL
 *
 * @param state
 * @returns {string}
 */
export const selectCteGalleryBackUrl = (state: State): string =>
  selectGalleryDexfSettings(state).CTE_GalleryBackURL;
